import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  Building2,
  Calculator,
  TrendingUp,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Search,
  GitCompare
} from 'lucide-react';
import { historicoService } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { formatPeriod, formatNumber } from '../utils/formatters';

// MM-YYYY → número ordenável (YYYYMM)
const periodoKey = (periodo) => {
  const [mes, ano] = periodo.split('-');
  return Number(ano) * 100 + Number(mes);
};

const MOTIVOS = {
  entrou: { label: 'Entrou no período', className: 'bg-blue-100 text-blue-800' },
  saiu: { label: 'Saiu (inativada)', className: 'bg-gray-200 text-gray-800' },
  sem_coleta_b: { label: 'Sem coleta no 2º período', className: 'bg-red-100 text-red-800' },
  sem_coleta_a: { label: 'Sem coleta no 1º período', className: 'bg-orange-100 text-orange-800' },
  variacao: { label: 'Variação de contas', className: 'bg-yellow-100 text-yellow-800' },
  igual: { label: 'Sem alteração', className: 'bg-green-100 text-green-800' }
};

const formatDelta = (valor) => {
  if (!valor) return '0';
  return `${valor > 0 ? '+' : ''}${formatNumber(valor)}`;
};

const deltaClass = (valor) => {
  if (valor > 0) return 'text-green-600';
  if (valor < 0) return 'text-red-600';
  return 'text-gray-500';
};

const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Diferença por empresa de uma franquia entre os dois períodos
const EmpresasDiff = ({ idFranquia, periodoA, periodoB }) => {
  const { data: empresasA, isLoading: loadingA } = useQuery(
    ['empresas-franquia', idFranquia, periodoA],
    () => historicoService.getEmpresasByFranquiaAndPeriodo(idFranquia, periodoA)
  );
  const { data: empresasB, isLoading: loadingB } = useQuery(
    ['empresas-franquia', idFranquia, periodoB],
    () => historicoService.getEmpresasByFranquiaAndPeriodo(idFranquia, periodoB)
  );

  const linhas = useMemo(() => {
    const mapa = new Map();
    const chave = (e) => e.cnpj || e.nome_empresa || e.ex_empresa;
    const total = (e) => (Number(e.total_locais) || 0) + (Number(e.total_cftv) || 0);

    (empresasA?.data || []).forEach((e) => {
      mapa.set(chave(e), { nome: e.nome_empresa, cnpj: e.cnpj, a: total(e), b: null });
    });
    (empresasB?.data || []).forEach((e) => {
      const atual = mapa.get(chave(e));
      if (atual) atual.b = total(e);
      else mapa.set(chave(e), { nome: e.nome_empresa, cnpj: e.cnpj, a: null, b: total(e) });
    });

    return [...mapa.values()]
      .map((l) => ({ ...l, diferenca: (l.b || 0) - (l.a || 0) }))
      .filter((l) => l.diferenca !== 0 || l.a === null || l.b === null)
      .sort((x, y) => Math.abs(y.diferenca) - Math.abs(x.diferenca));
  }, [empresasA, empresasB]);

  if (loadingA || loadingB) {
    return (
      <div className="py-4 flex justify-center">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (linhas.length === 0) {
    return (
      <p className="py-3 text-sm text-gray-500">
        Nenhuma empresa com diferença entre os períodos (ou não há detalhamento de empresas salvo).
      </p>
    );
  }

  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase text-gray-500">
          <th className="py-2 pr-4">Empresa</th>
          <th className="py-2 pr-4 text-right">{formatPeriod(periodoA)}</th>
          <th className="py-2 pr-4 text-right">{formatPeriod(periodoB)}</th>
          <th className="py-2 text-right">Diferença</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {linhas.map((l) => (
          <tr key={`${l.cnpj}-${l.nome}`}>
            <td className="py-2 pr-4">
              <div className="font-medium text-gray-800">{l.nome}</div>
              <div className="text-xs text-gray-500">
                {l.cnpj || 'Sem CNPJ'}
                {l.a === null && <span className="ml-2 text-blue-600">nova</span>}
                {l.b === null && <span className="ml-2 text-red-600">não veio na coleta</span>}
              </div>
            </td>
            <td className="py-2 pr-4 text-right">{l.a === null ? '—' : formatNumber(l.a)}</td>
            <td className="py-2 pr-4 text-right">{l.b === null ? '—' : formatNumber(l.b)}</td>
            <td className={`py-2 text-right font-semibold ${deltaClass(l.diferenca)}`}>
              {formatDelta(l.diferenca)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Comparativo = () => {
  const [periodoA, setPeriodoA] = useState('');
  const [periodoB, setPeriodoB] = useState('');
  const [somenteDiferencas, setSomenteDiferencas] = useState(true);
  const [busca, setBusca] = useState('');
  const [expandida, setExpandida] = useState(null);

  const { data: periodos } = useQuery('periodos-disponiveis', historicoService.getPeriodosDisponiveis);

  const periodosOrdenados = useMemo(() => {
    const lista = Array.isArray(periodos?.data) ? periodos.data : [];
    return [...lista].sort((a, b) => periodoKey(b) - periodoKey(a));
  }, [periodos]);

  // Padrão: mês anterior (A) x mês mais recente (B)
  useEffect(() => {
    if (periodosOrdenados.length > 0 && !periodoB) {
      setPeriodoB(periodosOrdenados[0]);
      setPeriodoA(periodosOrdenados[1] || periodosOrdenados[0]);
    }
  }, [periodosOrdenados, periodoB]);

  const { data: comparativo, isLoading } = useQuery(
    ['comparativo', periodoA, periodoB],
    () => historicoService.getComparativo(periodoA, periodoB),
    { enabled: !!periodoA && !!periodoB }
  );

  const dados = comparativo?.data;

  // Quanto da diferença total vem de cada motivo
  const decomposicao = useMemo(() => {
    if (!dados) return [];
    const grupos = {};
    dados.franquias.forEach((f) => {
      if (!grupos[f.motivo]) grupos[f.motivo] = { motivo: f.motivo, diferenca: 0, franquias: 0 };
      grupos[f.motivo].diferenca += f.diferenca;
      grupos[f.motivo].franquias += 1;
    });
    return Object.values(grupos)
      .filter((g) => g.motivo !== 'igual')
      .sort((a, b) => a.diferenca - b.diferenca);
  }, [dados]);

  const franquiasFiltradas = useMemo(() => {
    if (!dados) return [];
    const termo = busca.trim().toLowerCase();
    return dados.franquias
      .filter((f) => !somenteDiferencas || f.diferenca !== 0 || f.motivo !== 'igual')
      .filter((f) => !termo || f.nome_franquia.toLowerCase().includes(termo))
      .sort((a, b) => Math.abs(b.diferenca) - Math.abs(a.diferenca));
  }, [dados, busca, somenteDiferencas]);

  const cards = dados
    ? [
        {
          name: 'Total de Franquias',
          a: dados.resumo_a.total_franquias,
          b: dados.resumo_b.total_franquias,
          icon: Building2,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        },
        {
          name: 'Contas Monitoradas',
          a: dados.resumo_a.total_contas,
          b: dados.resumo_b.total_contas,
          icon: Calculator,
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        },
        {
          name: 'Média por Franquia',
          a: dados.resumo_a.media_por_franquia,
          b: dados.resumo_b.media_por_franquia,
          icon: TrendingUp,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100'
        }
      ]
    : [];

  const selectClass =
    'block w-40 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comparativo de Períodos</h1>
          <p className="text-sm text-gray-500">Entenda de onde vem a diferença de contas entre dois meses</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={periodoA} onChange={(e) => setPeriodoA(e.target.value)} className={selectClass}>
            {periodosOrdenados.map((p) => (
              <option key={p} value={p}>{formatPeriod(p)}</option>
            ))}
          </select>
          <ArrowRight className="w-5 h-5 text-gray-400" />
          <select value={periodoB} onChange={(e) => setPeriodoB(e.target.value)} className={selectClass}>
            {periodosOrdenados.map((p) => (
              <option key={p} value={p}>{formatPeriod(p)}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading || !dados ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Cards A → B */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {cards.map((card) => {
              const Icon = card.icon;
              const diferenca = card.b - card.a;
              const percentual = card.a ? ((diferenca / card.a) * 100).toFixed(1) : null;
              return (
                <div key={card.name} className="bg-white shadow-lg rounded-xl p-6">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-3 rounded-lg ${card.bgColor}`}>
                      <Icon className={`h-7 w-7 ${card.color}`} />
                    </div>
                    <div className="ml-5 flex-1">
                      <div className="text-sm font-medium text-gray-500">{card.name}</div>
                      <div className="flex items-baseline gap-2 text-gray-900">
                        <span className="text-lg text-gray-500">{formatNumber(card.a)}</span>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                        <span className="text-2xl font-bold">{formatNumber(card.b)}</span>
                      </div>
                      <div className={`text-sm font-semibold ${deltaClass(diferenca)}`}>
                        {formatDelta(diferenca)}
                        {percentual !== null && ` (${diferenca > 0 ? '+' : ''}${percentual}%)`}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Decomposição da diferença */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-1">De onde vem a diferença</h2>
            <p className="text-sm text-gray-500 mb-4">
              Variação total de contas de {formatPeriod(periodoA)} para {formatPeriod(periodoB)}:{' '}
              <span className={`font-semibold ${deltaClass(dados.resumo_b.total_contas - dados.resumo_a.total_contas)}`}>
                {formatDelta(dados.resumo_b.total_contas - dados.resumo_a.total_contas)}
              </span>
            </p>
            {decomposicao.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhuma diferença entre os períodos.</p>
            ) : (
              <div className="space-y-2">
                {decomposicao.map((g) => (
                  <div key={g.motivo} className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${MOTIVOS[g.motivo].className}`}>
                        {MOTIVOS[g.motivo].label}
                      </span>
                      <span className="text-sm text-gray-500">{g.franquias} franquia(s)</span>
                    </div>
                    <span className={`text-sm font-semibold ${deltaClass(g.diferenca)}`}>{formatDelta(g.diferenca)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tabela por franquia */}
          <div className="bg-white shadow rounded-lg">
            <div className="p-4 flex flex-col md:flex-row md:items-center gap-4 border-b border-gray-200">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar franquia..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={somenteDiferencas}
                  onChange={(e) => setSomenteDiferencas(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600"
                />
                Somente franquias com diferença
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <th className="px-6 py-3">Franquia</th>
                    <th className="px-6 py-3">Situação</th>
                    <th className="px-6 py-3 text-right">{formatPeriod(periodoA)}</th>
                    <th className="px-6 py-3 text-right">{formatPeriod(periodoB)}</th>
                    <th className="px-6 py-3 text-right">Diferença</th>
                    <th className="px-6 py-3">Última coleta</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {franquiasFiltradas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                        <GitCompare className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                        Nenhuma franquia para exibir.
                      </td>
                    </tr>
                  )}
                  {franquiasFiltradas.map((f) => {
                    const aberta = expandida === f.id_franquia;
                    return (
                      <React.Fragment key={f.id_franquia}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => setExpandida(aberta ? null : f.id_franquia)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {aberta ? (
                                <ChevronDown className="w-4 h-4 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="font-medium text-gray-900">{f.nome_franquia}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${MOTIVOS[f.motivo].className}`}>
                              {MOTIVOS[f.motivo].label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-700">
                            {f.ativa_a ? formatNumber(f.total_a) : '—'}
                          </td>
                          <td className="px-6 py-4 text-right text-sm text-gray-700">
                            {f.ativa_b ? formatNumber(f.total_b) : '—'}
                          </td>
                          <td className={`px-6 py-4 text-right text-sm font-semibold ${deltaClass(f.diferenca)}`}>
                            {formatDelta(f.diferenca)}
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-500">
                            <div>{formatPeriod(periodoA)}: {formatDateTime(f.atualizado_a)}</div>
                            <div>{formatPeriod(periodoB)}: {formatDateTime(f.atualizado_b)}</div>
                          </td>
                        </tr>
                        {aberta && (
                          <tr>
                            <td colSpan={6} className="px-12 pb-4 bg-gray-50">
                              <EmpresasDiff idFranquia={f.id_franquia} periodoA={periodoA} periodoB={periodoB} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Comparativo;
